import React, { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import * as Util from '../../../common/utilities/';
import * as StringUtils from '../../../utilities/stringUtils';
import { withFlags } from '../../../flags/Flags';

import {
  Accordion,
  AccordionPanel,
  Combobox
} from '@salesforce/design-system-react';

import { ExperimentAssetBrowser } from '../';
import './ExperimentBlockConfiguration.scss';

class ExperimentBlockConfiguration extends Component {
  constructor(props) {
    super(props);
    this.anchors = {
      info: React.createRef(),
      content: React.createRef(),
      settings: React.createRef()
    };

    this.state = {
      expandedPanels: {}
    };
  }

  getI18nID(suffix) {
    return `components.experiment.blocks.${suffix}`;
  }

  componentDidUpdate(prevProps, prevState) {
    // Update super content once a random asset is loaded
    if (
      Util.propChanged(
        prevProps,
        this.props,
        'experiments.experimentBlock.block'
      )
    ) {
      if (
        this.props.experiments?.experimentBlock?.block?.content_test_assets?.[0]
          ?.image_url
      ) {
        const element = this.superContentElement(
          this.props.experiments?.experimentBlock?.block
            ?.content_test_assets?.[0]?.name,
          this.props.experiments?.experimentBlock?.block
            ?.content_test_assets?.[0]?.image_url
        );
        this.props.actions.setSuperContentInSdk(element);
        this.props.actions.setExperimentContentInSdk(
          this.props.experiments?.experimentBlock?.block
        );
      }
    }

    ['info', 'content', 'settings'].forEach(section => {
      if (
        Util.propChanged(
          prevState,
          this.state,
          `expandedPanels.${section}`,
          true
        )
      ) {
        const ref = this.anchors[section];
        if (ref?.current) {
          ref.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  }

  handleExperimentChange(evt, data) {
    let updates = {
      experiment: { selection: data.selection }
    };
    this.props.formChange('selection', updates);
  }

  currentSelection() {
    const initSelection = { selection: [], inputValue: '' };
    const selection = this.props.forms?.selection;
    return (selection && selection.experiment) || initSelection;
  }

  selectionFor(experiment) {
    return {
      id: experiment.uuid,
      label: experiment.name,
      testMode: experiment.engine_name
    };
  }

  // Accepts a custom menu item rendering function that becomes a custom component.
  // It should return a React node.
  menuItem(props) {
    const { formatMessage } = this.props.intl;
    const testMode = formatMessage({
      id: this.getI18nID(`testMode.${props.option.testMode}`)
    });
    return (
      <div>
        <p className="test-name">{props.option.label}</p>
        <p className="test-mode">{testMode}</p>
      </div>
    );
  }

  superContentElement(experiment, superImageUrl) {
    const { formatMessage } = this.props.intl;
    const info = formatMessage({
      id: this.getI18nID('superContentInfo')
    });

    // Stylesheets and React components will not work inside super content, only raw html so we have to use
    // the style prop directly and ReactDOMServer.renderToStaticMarkup()
    const style = {
      left: '50%',
      position: 'absolute',
      backgroundColor: 'rgb(80,80,80, 0.6)',
      width: '55%',
      marginLeft: '-27%',
      display: 'flex',
      alignContent: 'center',
      alignItems: 'center',
      padding: '6px 10px 6px 10px',
      color: '#eeeeee',
      fontSize: '0.8rem',
      borderRadius: '2px'
    };

    const superContentBlock = (
      <div style={{ position: 'relative' }}>
        <div style={style}>
          <div style={{ marginRight: '4px' }}>
            <svg
              aria-hidden="true"
              style={{
                fill: '#eeeeee',
                width: '1.5rem',
                height: '1.5rem',
                lineHeight: '1'
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                id="preview"
              >
                <path d="M23.9 11.6C21.7 7.2 17.2 4.2 12 4.2S2.3 7.2.1 11.6c-.1.3-.1.6 0 .8 2.2 4.4 6.7 7.4 11.9 7.4s9.7-3 11.9-7.4c.1-.3.1-.5 0-.8zM12 17.1c-2.8 0-5.1-2.3-5.1-5.1S9.2 6.9 12 6.9s5.1 2.3 5.1 5.1-2.3 5.1-5.1 5.1zm0-8.3c-1.8 0-3.2 1.4-3.2 3.2s1.4 3.2 3.2 3.2 3.2-1.4 3.2-3.2-1.4-3.2-3.2-3.2z" />
              </svg>
            </svg>
          </div>
          {info}
        </div>
        <img
          alt="Sample Content"
          src={superImageUrl}
          width="100%"
          height="auto"
        />
      </div>
    );
    return ReactDOMServer.renderToStaticMarkup(superContentBlock);
  }

  renderTestInfo(experiment) {
    const infoName = this.getI18nID('configuration.info.name');
    const infoDescription = this.getI18nID('configuration.info.description');
    return (
      <div className="configuration-container" ref={this.anchors.blocksize}>
        <div className="test-info">
          <FormattedMessage id={infoName} />
          <p>{experiment?.name}</p>
          <FormattedMessage id={infoDescription} />
          <p>{experiment?.description}</p>
        </div>
      </div>
    );
  }

  renderTestContent(experiment) {
    const assets = experiment?.content_test_assets || [];
    const columns = [
      { name: 'thumbnail', sortable: false, width: '62px' },
      { name: 'name', sortable: false },
      { name: 'redirect_url', sortable: false }
    ];
    return (
      <div className="configuration-container" ref={this.anchors.blocksize}>
        <div className="test-info">
          <div className="assetpicker">
            <ExperimentAssetBrowser columns={columns} assets={assets} />
          </div>
        </div>
      </div>
    );
  }

  renderTestSettings(experiment) {
    const { formatMessage, formatNumber } = this.props.intl;
    const testModeTitle = this.getI18nID('configuration.settings.testMode');
    const endTriggerTitle = this.getI18nID(
      'configuration.settings.endTrigger.title'
    );
    const testEngine = experiment?.engine_name?.toLowerCase();
    const testTrigger =
      testEngine === 'ecs' ? 'ecs' : experiment?.end_trigger?.toLowerCase();
    const testMode = formatMessage({
      id: this.getI18nID(`configuration.settings.${testEngine}`)
    });
    const testWinner = formatMessage(
      {
        id: this.getI18nID(
          `configuration.settings.endTrigger.${testEngine}.${testTrigger}`
        )
      },
      { endValue: formatNumber(experiment?.end_value) }
    );
    return (
      <div className="configuration-container" ref={this.anchors.blocksize}>
        <div className="test-settings">
          <FormattedMessage id={testModeTitle} />
          <p>{testMode}</p>
          <FormattedMessage id={endTriggerTitle} />
          <p>{testWinner}</p>
        </div>
      </div>
    );
  }

  togglePanel(section) {
    const expandedPanels = { ...this.state.expandedPanels };
    expandedPanels[section] = !this.state.expandedPanels[section];
    this.setState({ expandedPanels });
  }

  renderSummary(experiment) {
    const { formatMessage, formatNumber } = this.props.intl;
    const numImages = experiment?.content_test_assets?.length || 0;

    return withFlags(flags => {
      const summary = {
        info: this.renderTestInfo(experiment),
        content: this.renderTestContent(experiment),
        settings: this.renderTestSettings(experiment)
      };

      return (
        <React.Fragment>
          <header className="configuration-header">
            <FormattedMessage id="components.experiment.blocks.configuration.summary" />
          </header>
          <Accordion className="experiment-summary">
            {Object.entries(summary)
              .filter(entry => !!entry[1])
              .map(entry => {
                const section = entry[0];
                const content = entry[1];
                const summary = formatMessage(
                  {
                    id: this.getI18nID(`configuration.${section}.title`)
                  },
                  { count: formatNumber(numImages) }
                );
                return (
                  <AccordionPanel
                    expanded={!!this.state.expandedPanels[section]}
                    id={section}
                    key={`accordion-${section}`}
                    onTogglePanel={() => this.togglePanel(section)}
                    summary={summary}
                  >
                    {content}
                  </AccordionPanel>
                );
              })}
          </Accordion>
        </React.Fragment>
      );
    });
  }

  ExperimentOptions() {
    const isSelectable = experiment =>
      experiment.active && experiment.status === 'PUBLISHED';

    const experiments =
      this.props.experiments?.search?.block?.experiments || [];

    return experiments
      .sort(StringUtils.sortByField('name', this.props.intl.locale))
      .filter(isSelectable)
      .map(this.selectionFor.bind(this));
  }

  render() {
    const { formatMessage } = this.props.intl;
    const experimentStrings = {
      title: formatMessage({
        id: this.getI18nID('configuration.title')
      })
    };
    const label = this.getI18nID('configuration.label');

    const disabled = this.props.forms?.selection?.isExistingBlock;
    const selection = this.currentSelection();

    return (
      <div>
        <div className="experiment">
          <header className="configuration-header">
            <span className="required">*</span>
            {experimentStrings.title}
          </header>
          <div className="configuration-container">
            <FormattedMessage id={label} />
            <Combobox
              classNameContainer="experiment-combobox"
              readonly
              singleInputDisabled={disabled}
              onRenderMenuItem={this.menuItem.bind(this)}
              options={this.ExperimentOptions()}
              selection={selection.selection}
              value={selection.inputValue}
              events={{
                onSelect: this.handleExperimentChange.bind(this)
              }}
              variant="readonly"
            />
          </div>
        </div>
        {selection.selection?.length > 0
          ? this.renderSummary(this.props.experiments?.experimentBlock?.block)
          : null}
      </div>
    );
  }
}

ExperimentBlockConfiguration.propTypes = {
  actions: PropTypes.object,
  experiments: PropTypes.object,
  forms: PropTypes.object,
  formChange: PropTypes.func,
  intl: intlShape.isRequired
};

export default injectIntl(ExperimentBlockConfiguration);
